// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Budget Model v2 (ADR-052 §11.2): euro amount parsing for the web inputs.
 * The German decimal comma is accepted ("45,50"), as is a dot ("45.50"), an
 * optional € sign and whitespace. Nothing is ever silently coerced: an
 * unparseable string is reported as `invalid` so the UI can say so instead
 * of dropping the field.
 */

export type ParsedEurInput =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: "cents"; cents: number }

export function parseEurInputToCents(raw: string): ParsedEurInput {
  const trimmed = raw.replace(/€/g, "").replace(/\s+/g, "").trim()
  if (trimmed === "") return { kind: "empty" }
  // Exactly one decimal separator, comma or dot, with at most two decimals.
  // Thousands separators are deliberately NOT interpreted: "45,505" would be
  // ambiguous between conventions, and a weekly grocery amount never needs
  // them — an ambiguous string is reported invalid, never guessed.
  const normalized = trimmed.replace(",", ".")
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return { kind: "invalid" }
  const cents = Math.round(Number(normalized) * 100)
  if (!Number.isFinite(cents) || cents < 0) return { kind: "invalid" }
  return { kind: "cents", cents }
}

/** Cents → the plain number the user typed/would type, locale-aware ("45,50" / "45.50"), no currency sign. */
export function centsToInputString(cents: number | null, locale: "en" | "de"): string {
  if (cents === null) return ""
  const whole = Math.floor(cents / 100)
  const frac = cents % 100
  const sep = locale === "de" ? "," : "."
  return frac === 0 ? String(whole) : `${whole}${sep}${String(frac).padStart(2, "0")}`
}
